import { useState, useEffect } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material"
import { PageLoader } from "@/components/ui/page-loader"
import { withCustomersAccess } from "@crm-src/components/page-permission-guard"

import {
  CreditCard,
  Users,
  MessageSquare,
  Activity,
  Contact,
  MoreHorizontal,
  Edit,
  X,
  RefreshCw,
  Receipt,
  FileText,
  KeyRound,
  ClipboardCheck,
  ClipboardList,
  BookOpen,
} from "lucide-react"

// Dashboard-side shared share components — relative imports (NOT `@/`, which the
// CRM tsconfig remaps to src/crm). See docs/crm.md + the share-to-chat spec.
import { buildEntityShareItems } from "../../../../components/share/entityShareItems"
import { ShareMenu } from "../../../../components/share/ShareMenu"
import { ShareToChatDialog } from "../../../../components/share/ShareToChatDialog"
import { customerShareSubject } from "../../../lib/shareSubject"

import { customersApi } from "@crm-src/lib/customers-api"
// Portal access controls live inline on each contact card now
// (see EditableContactInformationPanel + ContactPortalAccessButton).
import { dealsApi } from "@crm-src/lib/deals-api"
import type { DetailedCustomer, CustomerUpdateParams, CustomerSupportLevel, CustomerBusinessCategory } from "@crm-src/types/customer"
// Financial wall: route money through the one choke point. `useMoneyFormatter`
// yields a `(amount, options?) => string` formatter that redacts to "—" without
// financial access; `useFinancialAccess` drives hiding the whole Billing tab.
import { useFinancialAccess, useMoneyFormatter } from "@crm-src/components/money"

// Import panel components
import { FileManagerPanel } from "@crm-src/components/file-manager-panel"
import { NotesPanel } from "@crm-src/components/notes-panel"
import { ActivityLogPanel } from "@crm-src/components/activity-log-panel"
import { CustomerAgreementsPanel } from "@crm-src/components/customer-agreements-panel"
import { CustomerInvoicesTable } from "@crm-src/components/customer-invoices-table"
import { PurchaseOrdersTab } from "@crm-src/components/purchase-orders/purchase-orders-tab"
import { PreShipTab } from "@crm-src/components/pre-ship/pre-ship-tab"
import { CustomerFormModal } from "@crm-src/components/customer-form-modal"
import { EditAgreementModal } from "@crm-src/components/edit-agreement-modal"
import { CustomerLicencesPanel } from "@crm-src/components/customer-licences-panel"
import { DeleteCustomerModal } from "@crm-src/components/delete-customer-modal"
import { AddInvoiceModal } from "@crm-src/components/add-invoice-modal"
import { EditDealModal } from "@crm-src/components/edit-deal-modal"
import { EditableContactInformationPanel } from "@crm-src/components/editable-contact-information-panel"
import { TimezoneClock } from "@crm-src/components/timezone-clock"
import { ConvertToProspectModal } from "@crm-src/components/convert-to-prospect-modal"
import { CreateRecurringScheduleModal } from "@crm-src/components/create-recurring-schedule-modal"
import { EditRenewalDateModal } from "@crm-src/components/edit-renewal-date-modal"
import { invoiceSchedulesApi } from "@crm-src/lib/invoice-schedules-api"
import { DetailTabs } from "@crm-src/components/detail-tabs"
import { CustomerOverviewTab } from "@crm-src/components/customer-overview-tab"
import { useTabParam } from "@crm-src/hooks/useTabParam"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useCan } from "@/auth/permissions"

import type { Deal, ScheduledInvoiceInfo, RecurringScheduleInfo } from "@crm-src/components/customer-agreements-panel"
import type { DetailTab } from "@crm-src/components/detail-tabs"
import { BackButton } from "@/components/ui/back-button"

import { CustomerDocumentationTab } from "./CustomerDocumentationTab"

const CUSTOMER_TABS: DetailTab[] = [
  // Overview now shows Customer Information + Contacts side by side, so
  // there is no separate "Contacts" tab.
  { id: "overview", label: "Overview", icon: Users },
  // Agreements · Invoices · Quotes (2026-09-15) — each its own tab; they used to
  // be stacked on one "Billing" page. Financial wall: all three are money tabs,
  // filtered out of the visible set — and their panels skipped — for a viewer
  // without financial access (see `visibleTabs` below), exactly as Billing was.
  { id: "agreements", label: "Agreements", icon: CreditCard },
  { id: "invoices", label: "Invoices", icon: Receipt },
  { id: "quotes", label: "Quotes", icon: FileText },
  // Purchase Orders (2026-09-17): what the customer (or its reseller) sent XOP.
  { id: "purchase-orders", label: "Purchase Orders", icon: ClipboardList },
  // Licences (2026-09-15): every licence for this customer, a reseller's too,
  // with who issued it. Not money — it follows the licences page grant instead.
  { id: "licences", label: "Licences", icon: KeyRound },
  // Pre-ship (2026-10-03): the questionnaires this customer fills in before XOP
  // ships an appliance — several per customer (one per site). Customer data,
  // so it rides the page's own access; crm:edit to create and change.
  { id: "pre-ship", label: "Pre-ship", icon: ClipboardCheck },
  { id: "activity", label: "Activity", icon: Activity },
  // Notes + Files share one tab, side by side (no separate Files tab).
  { id: "notes", label: "Notes & Files", icon: MessageSquare },
  // What this customer reads on the support portal — the Access editor with
  // the account pre-picked (2026-09-30). Filtered out without
  // `documentation:manage_access`, like the reseller profile's tab.
  { id: "documentation", label: "Documentation", icon: BookOpen },
]

/** The money tabs — hidden entirely without financial access. */
const FINANCIAL_TABS = new Set(["agreements", "invoices", "quotes", "purchase-orders"])

const DOCUMENTATION_TAB = "documentation"

/** Tabs that no longer exist, and where their bookmarks land now. */
const LEGACY_TABS: Record<string, string> = { billing: "agreements" }

function CustomerDetailsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { canAccessLicences } = usePermissions()
  // WHO MANAGES A RECORD (CEO, 2026-09-16): "a salesperson should be capable
  // to do everything with an assigned customer". Management follows visibility
  // — every row on this page is one this user can see (crm-api narrows the list)
  // — so the controls follow `crm:edit`, not "is an administrator". crm-api
  // enforces the same rule (CustomerManagement); a read-only role such as
  // marketing_manager still gets no write controls.
  const canManage = useCan("crm:edit")
  const customerId = params.id as string

  // Financial wall: drop the money tabs (Agreements / Invoices / Quotes) and
  // their panels entirely for a viewer without financial access. Passing the
  // gated id list to useTabParam also means a direct `?tab=invoices` deep-link
  // falls back to Overview rather than rendering a hidden panel.
  const hasFinancialAccess = useFinancialAccess()
  const formatCurrency = useMoneyFormatter()
  const hasLicenceAccess = canAccessLicences()
  const canManageDocumentationAccess = useCan("documentation:manage_access")
  const visibleTabs = CUSTOMER_TABS.filter(
    (tab) =>
      (hasFinancialAccess || !FINANCIAL_TABS.has(tab.id)) &&
      (hasLicenceAccess || tab.id !== "licences") &&
      (canManageDocumentationAccess || tab.id !== DOCUMENTATION_TAB),
  )
  const visibleTabIds = visibleTabs.map((tab) => tab.id)
  const [activeTab, setActiveTab] = useTabParam(visibleTabIds, "overview")

  // A bookmark to a tab that was split up (`?tab=billing`) opens its successor
  // instead of silently landing on Overview — for a viewer who may see it.
  const [searchParams] = useSearchParams()
  const legacyTab = LEGACY_TABS[searchParams.get("tab") ?? ""]
  useEffect(() => {
    if (legacyTab && (hasFinancialAccess || !FINANCIAL_TABS.has(legacyTab))) setActiveTab(legacyTab)
  }, [legacyTab, hasFinancialAccess, setActiveTab])

  const [customer, setCustomer] = useState<DetailedCustomer | null>(null)
  const [loading, setLoading] = useState(true)

  // Actions menu anchor — replaces the shadcn DropdownMenu during the MUI 5
  // migration.
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null)

  // Edit dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [chatShareOpen, setChatShareOpen] = useState(false)
  const [isEditAgreementDialogOpen, setIsEditAgreementDialogOpen] = useState(false)
  
  // Delete dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  
  // Add invoice modal states
  const [isAddInvoiceModalOpen, setIsAddInvoiceModalOpen] = useState(false)
  const [selectedDealForInvoice, setSelectedDealForInvoice] = useState<Deal | null>(null)
  
  // Create deal modal states
  const [isCreateDealDialogOpen, setIsCreateDealDialogOpen] = useState(false)
  
  // Convert to prospect modal states
  const [isConvertToProspectModalOpen, setIsConvertToProspectModalOpen] = useState(false)
  const [isConverting, setIsConverting] = useState(false)

  // Scheduling modal states
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false)
  const [selectedInvoiceForSchedule, setSelectedInvoiceForSchedule] = useState<any>(null)

  // Renewal date modal state
  const [isRenewalDateModalOpen, setIsRenewalDateModalOpen] = useState(false)
  const [selectedDealForRenewal, setSelectedDealForRenewal] = useState<{ id: string, date: string | null } | null>(null)
  const [isSendingReminder, setIsSendingReminder] = useState(false)

  // Scheduled invoices data
  const [nextScheduledInvoice, setNextScheduledInvoice] = useState<ScheduledInvoiceInfo | null>(null)
  const [recurringSchedules, setRecurringSchedules] = useState<RecurringScheduleInfo[]>([])

  // Deals will come from customer data

  // Load customer data
  const loadCustomer = async () => {
    try {
      setLoading(true)
      const response = await customersApi.getById(customerId)
      setCustomer(response.customer)
    } catch (error) {
      console.error('Error loading customer:', error)
      toast({
        title: "Error",
        description: "Failed to load customer details",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Load scheduled invoices for this customer
  const loadScheduledInvoices = async () => {
    try {
      // Get upcoming scheduled invoices for this customer
      const upcomingResponse = await invoiceSchedulesApi.getUpcoming({
        customer_id: customerId,
        limit: 5,
        days: 90
      })

      if (upcomingResponse.success && upcomingResponse.data && upcomingResponse.data.length > 0) {
        // Get the next scheduled invoice
        const next = upcomingResponse.data[0]
        setNextScheduledInvoice({
          id: next.id,
          scheduled_date: next.scheduled_date,
          status: next.status,
          recipient_email: next.recipient_email,
          source_invoice: next.source_invoice,
          invoice_schedule: next.invoice_schedule
        })
      } else {
        setNextScheduledInvoice(null)
      }

      // Get recurring schedules for this customer
      const schedulesResponse = await invoiceSchedulesApi.getAll({
        customer_id: customerId,
        status: 'active'
      })

      if (schedulesResponse.success && schedulesResponse.data) {
        setRecurringSchedules(schedulesResponse.data.map((s: any) => ({
          id: s.id,
          name: s.name,
          frequency: s.frequency,
          status: s.status,
          next_scheduled_at: s.next_scheduled_at,
          source_invoice: s.source_invoice
        })))
      }
    } catch (error) {
      console.error('Error loading scheduled invoices:', error)
      // Don't show error toast - this is supplementary data
    }
  }

  useEffect(() => {
    if (customerId) loadCustomer()
  }, [customerId, toast])

  // Financial wall: scheduled invoices are monetary. The Billing tab is hidden
  // for a viewer without `crm:financials`, and the fetch must be skipped too —
  // `loadScheduledInvoices` hits the financial `/scheduled_invoices` +
  // `/invoice_schedules` endpoints, which the wall 403s for a non-financial role
  // (e.g. marketing_manager). The error was swallowed here (line below), but the
  // forbidden request still fired on every customer open. Mirrors the prospect
  // Quotes gating.
  useEffect(() => {
    if (customerId && hasFinancialAccess) loadScheduledInvoices()
  }, [customerId, hasFinancialAccess])

  // Refresh customer data after updates
  const refreshCustomerData = async () => {
    try {
      const response = await customersApi.getById(customerId)
      setCustomer(response.customer)
    } catch (error) {
      console.error('❌ Error refreshing customer:', error)
      toast({
        title: "Error",
        description: "Failed to refresh customer data",
        variant: "destructive",
      })
    }
  }

  // Inline edit (Overview tab): assign salesperson / change business type in
  // place without opening the edit modal. Both throw on failure so the shared
  // <InlineEditField> surfaces the error toast; on success we refresh.
  // See docs/crm.md → "Inline editing".
  const handleInlineAssignSalespeople = async (userIds: string[]) => {
    await customersApi.bulkAssignSalespeople([customerId], userIds)
    await refreshCustomerData()
    toast({ title: "Saved", description: "Salesperson updated" })
  }

  const handleInlineChangeBusinessCategory = async (value: CustomerBusinessCategory) => {
    await customersApi.update(customerId, { business_category: value })
    await refreshCustomerData()
    toast({ title: "Saved", description: "Business type updated" })
  }

  const handleInlineChangeIndustry = async (value: string) => {
    await customersApi.update(customerId, { industry: value })
    await refreshCustomerData()
    toast({ title: "Saved", description: "Industry updated" })
  }

  // Handle opening edit dialog
  const handleEditCustomer = () => {
    setIsEditDialogOpen(true)
  }

  // Handle opening edit agreement dialog
  const handleEditAgreement = () => {
    setIsEditAgreementDialogOpen(true)
  }

  // Handle customer update from modal
  const handleCustomerUpdated = async (updatedCustomer: DetailedCustomer) => {
    // Refresh complete customer data to ensure deals and other related data are included
    await refreshCustomerData()
  }

  // Handle agreement update from modal
  const handleAgreementUpdated = async (updatedCustomer: DetailedCustomer) => {
    // Refresh complete customer data to ensure deals and other related data are included
    await refreshCustomerData()
  }

  // Handle customer deletion
  const handleDeleteCustomer = () => {
    setIsDeleteDialogOpen(true)
  }

  // Handle convert to prospect - show confirmation modal
  const handleConvertToProspect = () => {
    setIsConvertToProspectModalOpen(true)
  }

  // Handle setting up recurring invoices
  const handleSetupRecurring = () => {
    // Get the most recent invoice for this customer to use as template
    const invoices = customer?.customer_deals?.[0]?.invoices || []
    const mostRecentInvoice = invoices[0]

    if (mostRecentInvoice) {
      setSelectedInvoiceForSchedule({
        id: mostRecentInvoice.id,
        invoice_number: mostRecentInvoice.number,
        total_amount: mostRecentInvoice.total_amount,
        customer_id: customerId,
        customer_name: customer?.full_name,
        customer_email: customer?.email
      })
      setIsRecurringModalOpen(true)
    } else {
      // No existing invoice - prompt to create one first
      toast({
        title: "No Invoice Available",
        description: "Please create an invoice first, then you can set up recurring billing.",
        variant: "default",
      })
      navigate(`/crm/invoices/generate?customerId=${customerId}`)
    }
  }

  // Handle viewing a scheduled invoice
  const handleViewScheduledInvoice = (scheduledId: string) => {
    navigate(`/crm/invoices/scheduled/${scheduledId}`)
  }

  // Handle viewing all schedules for this customer
  const handleViewAllSchedules = () => {
    navigate(`/crm/invoices/schedules?customer_id=${customerId}`)
  }

  // Handle scheduling success - refresh data
  const handleSchedulingSuccess = () => {
    loadScheduledInvoices()
    toast({
      title: "Success",
      description: "Invoice scheduling updated",
    })
  }

  // Handle editing AMC renewal date
  const handleEditRenewalDate = (dealId: string, currentDate: string | null) => {
    setSelectedDealForRenewal({ id: dealId, date: currentDate })
    setIsRenewalDateModalOpen(true)
  }

  // Handle saving renewal date
  const handleSaveRenewalDate = async (dealId: string, renewalDate: string | null) => {
    try {
      await dealsApi.updateDeal(dealId, { renewal_date: renewalDate })
      toast({
        title: "Success",
        description: renewalDate
          ? "AMC renewal date updated. A reminder task will be created automatically."
          : "AMC renewal date cleared.",
      })
      // Refresh customer data to reflect changes
      await refreshCustomerData()
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update renewal date",
        variant: "destructive",
      })
      throw error
    }
  }

  // Handle sending renewal reminder manually
  const handleSendRenewalReminder = async (dealId: string) => {
    try {
      setIsSendingReminder(true)
      const result = await dealsApi.sendRenewalReminder(dealId)
      toast({
        title: "Reminder Sent",
        description: `${result.message}${result.recipients ? ` (${result.recipients.join(', ')})` : ''}`,
      })
    } catch (error: any) {
      const status = error.response?.status
      const errorMessage = error.response?.data?.error

      if (status === 429) {
        // Rate limited - reminder already sent recently
        toast({
          title: "Already Sent",
          description: errorMessage || "Reminder was already sent recently. Please wait before sending again.",
        })
      } else {
        toast({
          title: "Error",
          description: errorMessage || error.message || "Failed to send reminder",
          variant: "destructive",
        })
      }
    } finally {
      setIsSendingReminder(false)
    }
  }

  // Handle confirmed conversion to prospect
  const handleConfirmConvertToProspect = async () => {
    try {
      setIsConverting(true)
      const response = await customersApi.convertToProspect(customerId, { force_delete_agreements: true })
      
      toast({
        title: "Success",
        description: response.message || "Customer converted to prospect successfully",
      })
      
      // Close modal and redirect to prospects page
      setIsConvertToProspectModalOpen(false)
      navigate('/crm/prospects')
    } catch (error: any) {
      console.error('Error converting customer to prospect:', error)
      
      const errorData = error?.response?.data
      const errorMessage = errorData?.error || errorData?.errors?.[0] || 'Failed to convert customer to prospect'
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsConverting(false)
    }
  }

  // Handle navigation after successful deletion
  const handleNavigateToList = () => {
    navigate('/crm/customers')
  }

  // Handle add invoice
  const handleAddInvoice = (dealId: string) => {
    // Find the deal to get its details
    const deal = customer?.customer_deals?.find(d => d.id === dealId)
    if (deal) {
      setSelectedDealForInvoice(deal)
      setIsAddInvoiceModalOpen(true)
    }
  }

  // Handle invoice created
  const handleInvoiceCreated = (invoice: any) => {
    // Refresh customer data to show the new invoice
    refreshCustomerData()
  }

  // Handle create new deal
  const handleCreateDeal = () => {
    setIsCreateDealDialogOpen(true)
  }

  // Handle deal creation
  const handleDealCreated = async (dealData: any) => {
    try {
      
      // Prepare deal data for API  
      const createDealData = {
        customer_id: customerId,
        title: dealData.name,
        amount: dealData.amount,
        equipment_price: dealData.amount, // Set both amount and equipment_price
        maintenance_percentage: dealData.maintenance_percentage,
        start_date: dealData.start_date,
        end_date: dealData.end_date, // Map end_date if provided
        ...(dealData.renewal_date && { renewal_date: dealData.renewal_date }), // ✅ FIXED: Send renewal_date correctly
        deal_type: 'simple_purchase' // Default deal type
      }

      
      // Create the deal
      const result = await dealsApi.createDeal(createDealData)
      
      // Refresh data - EditDealModal handles modal closing and success message
      await refreshCustomerData()
    } catch (error: any) {
      console.error('Error creating deal:', error)
      toast({
        title: "Error",
        description: error.response?.data?.errors?.join(', ') || error.message || "Failed to create deal",
        variant: "destructive"
      })
      throw error // Re-throw so modal can handle loading state
    }
  }


  if (loading) {
    return <PageLoader />
  }

  if (!customer) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6 }}>
        <Typography component="h2" sx={{ fontSize: "1.25rem", fontWeight: 600, color: "text.primary", mb: 1 }}>Customer not found</Typography>
        <Typography sx={{ color: "text.secondary", mb: 2 }}>The customer you're looking for doesn't exist or has been removed.</Typography>
        <BackButton label="Go Back" />
      </Box>
    )
  }

  // Format support level for display
  const formatSupportLevel = (supportLevel: CustomerSupportLevel) => {
    switch (supportLevel) {
      case 'none':
        return 'NONE'
      case 'weekdays':
        return 'WEEK DAYS'
      case '24_7':
        return '24/7'
      default:
        return 'NONE'
    }
  }

  // Get support level badge color
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header: back + record-level actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <BackButton />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShareMenu
            items={buildEntityShareItems({
              subject: customerShareSubject(customer),
              onShareToChat: () => setChatShareOpen(true),
              notify: (m) => toast(m),
            })}
          />
          <Button variant="outlined" color="inherit" size="small" startIcon={<MoreHorizontal size={16} />} onClick={(e) => setActionsAnchor(e.currentTarget)}>
            Actions
          </Button>
        </Box>
        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={() => setActionsAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Customer Actions</ListSubheader>
          <MenuItem onClick={() => { setActionsAnchor(null); handleEditCustomer(); }}>
            <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
            Edit Customer
          </MenuItem>
          <MenuItem onClick={() => { setActionsAnchor(null); handleConvertToProspect(); }}>
            <Box sx={{ mr: 1, display: "flex" }}><RefreshCw size={16} /></Box>
            Convert to Prospect
          </MenuItem>
          <Divider />
          <MenuItem
            sx={{ color: "error.main" }}
            onClick={() => { setActionsAnchor(null); handleDeleteCustomer(); }}
          >
            <Box sx={{ mr: 1, display: "flex" }}><X size={16} /></Box>
            Delete Customer
          </MenuItem>
        </Menu>
      </Box>

      {/* Identity */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          src={`https://api.dicebear.com/7.x/initials/svg?seed=${customer.company_name || customer.full_name}`}
          sx={{ width: 56, height: 56 }}
        >
          {customer.company_name
            ? customer.company_name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
            : `${customer.first_name?.[0] || 'U'}${customer.last_name?.[0] || 'N'}`}
        </Avatar>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>{customer.company_name || 'Unknown Company'}</Typography>
          <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem", bgcolor: "action.hover", px: 1, py: 0.5, borderRadius: 1, display: "inline-block", mt: 0.5 }}>{customer.customer_id || customer.id}</Box>
        </Box>
      </Box>

      {/* Section tabs (Billing filtered out without financial access) */}
      <DetailTabs
        tabs={visibleTabs}
        activeId={activeTab}
        onTabChange={setActiveTab}
        ariaLabel="Customer sections"
      />

      {/* Active tab content */}
      {activeTab === 'overview' && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
          <CustomerOverviewTab
            customer={customer}
            editable={canManage}
            onAssignSalespeople={handleInlineAssignSalespeople}
            onChangeBusinessCategory={handleInlineChangeBusinessCategory}
            onChangeIndustry={handleInlineChangeIndustry}
          />
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Contact size={20} />
                  <Typography component="span" sx={{ fontWeight: 600 }}>Contacts</Typography>
                </Box>
              }
            />
            <CardContent>
              <EditableContactInformationPanel
                data={customer}
                customerId={customer.id}
                onContactsUpdated={(contacts) => {
                  setCustomer(prev => prev ? { ...prev, customer_contacts: contacts } : null)
                }}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Financial wall: the three money tabs render only for a viewer with
          financial access (defense in depth — the tabs are already unreachable
          without it). */}
      {activeTab === 'agreements' && hasFinancialAccess && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Licences have their own tab now (below). The agreement is what this
              customer bought; a licence is what one appliance may run. */}
          <CustomerAgreementsPanel
            deals={customer.customer_deals || []}
            customerName={customer.full_name}
            companyName={customer.company_name}
            customerId={customer.id.toString()}
            customerEmail={customer.email}
            supportLevel={customer.support_level}
            slaFirstResponseMinutes={customer.effective_sla_first_response_minutes}
            slaResolutionMinutes={customer.effective_sla_resolution_minutes}
            serverCount={customer.server_count}
            reseller={customer.reseller}
            product={customer.product}
            release={customer.installed_release_version}
            features={customer.features}
            dealers={customer.dealers}
            nextScheduledInvoice={nextScheduledInvoice}
            recurringSchedules={recurringSchedules}
            formatCurrency={formatCurrency}
            onEditAgreement={handleEditAgreement}
            onCreateAgreement={handleCreateDeal}
            onSetupRecurring={handleSetupRecurring}
            onViewScheduledInvoice={handleViewScheduledInvoice}
            onViewAllSchedules={handleViewAllSchedules}
            onEditRenewalDate={handleEditRenewalDate}
            onSendRenewalReminder={handleSendRenewalReminder}
            isSendingReminder={isSendingReminder}
          />
        </Box>
      )}

      {/* Invoices and Quotes: one server-side paginated + sortable table per tab.
          Row mutations (delete / download / mark paid|unpaid) live inside the
          table; `onChanged` re-reads the customer so the agreement stays in step. */}
      {activeTab === 'invoices' && hasFinancialAccess && (
        <CustomerInvoicesTable
          customerId={customer.id.toString()}
          documentType="invoice"
          onChanged={refreshCustomerData}
          formatCurrency={formatCurrency}
          recurringInvoiceIds={recurringSchedules
            .filter((schedule) => schedule.status === 'active' || schedule.status === 'paused')
            .map((schedule) => schedule.source_invoice?.id?.toString())
            .filter((id): id is string => !!id)}
        />
      )}

      {activeTab === 'quotes' && hasFinancialAccess && (
        <CustomerInvoicesTable
          customerId={customer.id.toString()}
          documentType="quote"
          onChanged={refreshCustomerData}
          formatCurrency={formatCurrency}
        />
      )}

      {activeTab === 'purchase-orders' && hasFinancialAccess && (
        <PurchaseOrdersTab
          customerId={customer.id.toString()}
          customerName={customer.company_name || customer.full_name}
        />
      )}

      {activeTab === 'pre-ship' && (
        <PreShipTab customerId={customer.id.toString()} />
      )}

      {/* Licences: every licence for this customer, whoever issued it. The
          panel also gates itself on the licences page grant. */}
      {activeTab === 'licences' && hasLicenceAccess && (
        <CustomerLicencesPanel
          customerId={Number(customerId)}
          customerName={customer.company_name || customer.full_name}
        />
      )}

      {activeTab === 'activity' && (
        <ActivityLogPanel
          customerId={customerId}
          customerName={customer.full_name}
          showScheduleButton={true}
          autoRefresh={true}
        />
      )}

      {activeTab === 'notes' && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
          <NotesPanel
            customerId={customer.id}
            currentUserName="Current User"
          />
          <FileManagerPanel
            customerId={customer.id}
          />
        </Box>
      )}

      {/* Documentation: the Access editor for THIS customer, addressed by its
          crm id. Gated twice, like every filtered tab (defense in depth). */}
      {activeTab === DOCUMENTATION_TAB && canManageDocumentationAccess && (
        <CustomerDocumentationTab
          customerId={customer.id.toString()}
          customerName={customer.company_name || customer.full_name}
        />
      )}

      {/* Edit Customer Modal */}
      <CustomerFormModal
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        customer={customer}
        onSuccess={handleCustomerUpdated}
      />

      {/* Edit Agreement Modal */}
      <EditAgreementModal
        isOpen={isEditAgreementDialogOpen}
        onClose={() => setIsEditAgreementDialogOpen(false)}
        customer={customer}
        onAgreementUpdated={handleAgreementUpdated}
      />

      {/* Delete Customer Modal */}
      <DeleteCustomerModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        customer={customer}
        onNavigateToList={handleNavigateToList}
      />

      {/* Add Invoice Modal */}
      <AddInvoiceModal
        isOpen={isAddInvoiceModalOpen}
        onClose={() => setIsAddInvoiceModalOpen(false)}
        deal={selectedDealForInvoice}
        customerId={customerId}
        onInvoiceCreated={handleInvoiceCreated}
      />

      {/* Create Deal Modal */}
      <EditDealModal
        open={isCreateDealDialogOpen}
        onOpenChange={setIsCreateDealDialogOpen}
        customerName={customer?.full_name}
        companyName={customer?.company_name}
        onSave={handleDealCreated}
      />

      {/* Convert to Prospect Modal */}
      <ConvertToProspectModal
        isOpen={isConvertToProspectModalOpen}
        onClose={() => setIsConvertToProspectModalOpen(false)}
        onConfirm={handleConfirmConvertToProspect}
        customerName={customer?.full_name || customer?.company_name || 'this customer'}
        agreementCount={customer?.customer_deals?.length || 0}
        invoiceCount={customer?.customer_deals?.reduce((total, deal) => total + (deal.invoices?.length || 0), 0) || 0}
        isLoading={isConverting}
      />

      {/* Create Recurring Schedule Modal */}
      <CreateRecurringScheduleModal
        isOpen={isRecurringModalOpen}
        onClose={() => setIsRecurringModalOpen(false)}
        onSuccess={handleSchedulingSuccess}
        invoice={selectedInvoiceForSchedule}
        customerId={customerId}
        customerName={customer?.full_name}
        customerEmail={customer?.email}
      />

      {/* Edit Renewal Date Modal */}
      <EditRenewalDateModal
        isOpen={isRenewalDateModalOpen}
        onClose={() => {
          setIsRenewalDateModalOpen(false)
          setSelectedDealForRenewal(null)
        }}
        onSave={handleSaveRenewalDate}
        dealId={selectedDealForRenewal?.id || null}
        currentDate={selectedDealForRenewal?.date || null}
        customerName={customer?.full_name}
      />

      {/* Share this customer into the internal staff chat */}
      <ShareToChatDialog
        open={chatShareOpen}
        onClose={() => setChatShareOpen(false)}
        subject={customerShareSubject(customer)}
      />

    </Box>
  )
}

export default withCustomersAccess(CustomerDetailsPage)
